/**
 * usePermissions Hook
 * 
 * Provides permission checking for the current user based on their
 * subscription tier and role within the current organisation.
 * 
 * Usage:
 *   const { can, canWithReason, tier, role } = usePermissions();
 *   
 *   if (can(FEATURES.EMISSIONS_ADD_ENTRY)) { ... }
 *   
 *   const { allowed, reason, upgradeTarget } = canWithReason(FEATURES.OFFSETS_VIEW);
 *   // reason: 'allowed' | 'denied' | 'upgrade:pro' | 'upgrade:standard_or_pro' | 'hidden' | 'coming_soon'
 */

import { useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
    getAccess,
    isAllowed,
    isHidden,
    isUpgradeRequired,
    getUpgradeTarget,
    FEATURES,
} from './permissionsMatrix';

/**
 * Hook that reads tier and role from MemberLayout's outlet context
 * and provides permission-checking helpers.
 */
export function usePermissions() {
    const context = useOutletContext() || {};

    const {
        subscriptionStatus,
        userRole,
        currentOrg,
    } = context;

    // Derive effective tier
    const tier = useMemo(() => {
        // Check for subscription override first
        if (currentOrg?.subscription_override) {
            const overrideExpiry = currentOrg.subscription_override_expiry;
            if (!overrideExpiry || new Date(overrideExpiry) > new Date()) {
                return currentOrg.subscription_override.replace('manual_', '');
            }
        }
        return subscriptionStatus?.tier || currentOrg?.subscription_tier || 'demo';
    }, [subscriptionStatus, currentOrg]);

    // Derive effective role
    const role = useMemo(() => {
        return userRole?.role || 'viewer';
    }, [userRole]);

    // Is this user an admin or superadmin globally?
    const isAdmin = useMemo(() => {
        return userRole?.is_superadmin === true;
    }, [userRole]);

    /**
     * Check if the user can perform a feature action.
     * Returns true if allowed, false otherwise.
     */
    const can = useMemo(() => {
        return (feature) => {
            // Admin panel bypass
            if (isAdmin && feature === FEATURES.SETTINGS_ADMIN_PANEL) {
                return true;
            }
            const access = getAccess(feature, tier, role);
            return isAllowed(access);
        };
    }, [tier, role, isAdmin]);

    /**
     * Check permission with detailed reason for UI rendering.
     * 
     * Returns: {
     *   allowed: boolean,
     *   reason: 'allowed' | 'denied' | 'upgrade:pro' | 'upgrade:standard_or_pro' | 'hidden' | 'coming_soon',
     *   upgradeTarget: 'pro' | 'standard_or_pro' | null,
     *   isHidden: boolean
     * }
     */
    const canWithReason = useMemo(() => {
        return (feature) => {
            // Admin panel bypass
            if (isAdmin && feature === FEATURES.SETTINGS_ADMIN_PANEL) {
                return { allowed: true, reason: 'allowed', upgradeTarget: null, isHidden: false };
            }

            const access = getAccess(feature, tier, role);

            if (isAllowed(access)) {
                return { allowed: true, reason: 'allowed', upgradeTarget: null, isHidden: false };
            }

            if (isHidden(access)) {
                return { allowed: false, reason: 'hidden', upgradeTarget: null, isHidden: true };
            }

            if (access === 'coming_soon') {
                return { allowed: false, reason: 'coming_soon', upgradeTarget: null, isHidden: false };
            }

            if (isUpgradeRequired(access)) {
                const target = getUpgradeTarget(access);
                return { allowed: false, reason: access, upgradeTarget: target, isHidden: false };
            }

            // Role-restricted (false)
            return { allowed: false, reason: 'denied', upgradeTarget: null, isHidden: false };
        };
    }, [tier, role, isAdmin]);

    return {
        can,
        canWithReason,
        tier,
        role,
        isAdmin,
        // Convenience booleans (backward compat)
        isPro: tier === 'pro',
        isStandard: tier === 'standard',
        isDemo: tier === 'demo',
        isOwner: role === 'owner',
    };
}

// Re-export FEATURES for convenient imports
export { FEATURES } from './permissionsMatrix';

export default usePermissions;
